import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { planContext } from "@/lib/plan";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Pencil, Download, Coins, Scale, ArrowLeft, Building2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "recuperable", label: "Récupérable (locataire)", color: "bg-teal-100 text-teal-800" },
  { value: "proprietaire", label: "Propriétaire (bailleur)", color: "bg-slate-100 text-slate-700" },
];
const catMeta = (v) => CATEGORIES.find((c) => c.value === v) || CATEGORIES[1];

function today() { return new Date().toISOString().slice(0, 10); }
const YEARS = [0, 1, 2].map((i) => new Date().getFullYear() - i);

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canWrite } = planContext(user);

  const [property, setProperty] = useState(null);
  const [leases, setLeases] = useState([]);
  const [charges, setCharges] = useState([]);
  const [regs, setRegs] = useState([]);

  // Dialog dépense
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ label: "", amount: "", date: today(), category: "recuperable", note: "" });

  // Régularisation
  const [regLease, setRegLease] = useState("");
  const [regYear, setRegYear] = useState(String(new Date().getFullYear()));
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const [p, l, c, r] = await Promise.all([
        api.get(`/properties/${id}`),
        api.get("/leases"),
        api.get(`/charges?property_id=${id}`),
        api.get("/regularisation"),
      ]);
      setProperty(p.data);
      const own = l.data.filter((x) => x.property_id === id);
      setLeases(own);
      setCharges(c.data);
      setRegs(r.data.filter((x) => x.property_id === id));
      if (!regLease && own[0]) setRegLease(own[0].id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bien introuvable");
      navigate("/app/biens");
    }
  };
  const loadCharges = async () => {
    const { data } = await api.get(`/charges?property_id=${id}`);
    setCharges(data);
  };
  const loadRegs = async () => {
    const { data } = await api.get("/regularisation");
    setRegs(data.filter((x) => x.property_id === id));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const totals = useMemo(() => {
    let rec = 0, prop = 0;
    charges.forEach((c) => {
      if (c.category === "recuperable") rec += Number(c.amount || 0);
      else prop += Number(c.amount || 0);
    });
    return { rec, prop };
  }, [charges]);

  const openNew = () => {
    if (!canWrite) return;
    setEditId(null);
    setForm({ label: "", amount: "", date: today(), category: "recuperable", note: "" });
    setOpen(true);
  };
  const openEdit = (c) => {
    if (!canWrite) return;
    setEditId(c.id);
    setForm({ label: c.label, amount: String(c.amount), date: c.date, category: c.category, note: c.note || "" });
    setOpen(true);
  };
  const saveCharge = async () => {
    if (!form.label.trim() || form.amount === "" || !form.date) return toast.error("Libellé, montant et date requis");
    const payload = {
      property_id: id, label: form.label.trim(), amount: Number(form.amount),
      date: form.date, category: form.category, note: form.note,
    };
    try {
      if (editId) await api.put(`/charges/${editId}`, payload);
      else await api.post("/charges", payload);
      toast.success(editId ? "Dépense mise à jour" : "Dépense ajoutée");
      setOpen(false);
      loadCharges();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const delCharge = async (cid) => {
    if (!canWrite) return;
    if (!window.confirm("Supprimer cette dépense ?")) return;
    try { await api.delete(`/charges/${cid}`); loadCharges(); toast.success("Dépense supprimée"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  // ----- Régularisation ----- //
  const runPreview = async (lease_id, year) => {
    if (!lease_id || !year) { setPreview(null); return; }
    try {
      const { data } = await api.get(`/regularisation/preview?lease_id=${lease_id}&year=${year}`);
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); setPreview(null); }
  };
  useEffect(() => { runPreview(regLease, regYear); /* eslint-disable-next-line */ }, [regLease, regYear]);

  const generate = async () => {
    if (!canWrite) return;
    if (!regLease) return toast.error("Sélectionnez un bail");
    try {
      await api.post("/regularisation", { lease_id: regLease, year: Number(regYear) });
      toast.success("Décompte de régularisation généré — visible par le locataire");
      loadRegs();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const delReg = async (rid) => {
    if (!canWrite) return;
    if (!window.confirm("Supprimer ce décompte ?")) return;
    try { await api.delete(`/regularisation/${rid}`); setRegs((r) => r.filter((x) => x.id !== rid)); toast.success("Décompte supprimé"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const downloadReg = async (rid, year) => {
    try {
      const res = await api.get(`/regularisation/${rid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `regularisation-${year}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const leaseLabel = (l) => l.tenant_name || "Locataire";

  return (
    <div className="space-y-6" data-testid="property-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/app/biens")} data-testid="property-detail-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight truncate">{property?.label || "Bien"}</h1>
            <p className="text-sm text-slate-500 truncate">{property ? `${property.address || ""}${property.address ? ", " : ""}${property.postal_code || ""} ${property.city || ""}` : ""}</p>
          </div>
        </div>
      </div>

      {/* ---------- Dépenses du bien ---------- */}
      <Card className="p-5" data-testid="charges-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-teal-700" />
            <h2 className="font-heading font-bold text-lg">Dépenses du bien</h2>
          </div>
          <Button onClick={openNew} disabled={!canWrite} data-testid="add-charge-btn">
            <Plus className="h-4 w-4 mr-1" /> Dépense
          </Button>
        </div>

        <div className="flex flex-wrap gap-3 mb-4">
          <div className="rounded-xl border border-teal-200 bg-teal-50/60 px-4 py-3">
            <div className="text-xs text-teal-700 font-medium">Récupérable (locataire)</div>
            <div className="font-heading font-bold text-xl text-teal-900" data-testid="total-recuperable">{fmtEUR(totals.rec)}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-xs text-slate-600 font-medium">Propriétaire (bailleur)</div>
            <div className="font-heading font-bold text-xl text-slate-900" data-testid="total-proprietaire">{fmtEUR(totals.prop)}</div>
          </div>
        </div>

        {charges.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">Aucune dépense enregistrée pour ce bien.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead><TableHead>Libellé</TableHead>
                  <TableHead>Nature</TableHead><TableHead className="text-right">Montant</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {charges.map((c) => (
                  <TableRow key={c.id} data-testid={`charge-row-${c.id}`}>
                    <TableCell>{fmtDate(c.date)}</TableCell>
                    <TableCell className="font-medium text-slate-800">{c.label}</TableCell>
                    <TableCell><Badge className={catMeta(c.category).color}>{catMeta(c.category).label}</Badge></TableCell>
                    <TableCell className="text-right font-medium">{fmtEUR(c.amount)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} disabled={!canWrite} data-testid={`edit-charge-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => delCharge(c.id)} disabled={!canWrite} data-testid={`delete-charge-${c.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* ---------- Régularisation annuelle ---------- */}
      <Card className="p-5" data-testid="regularisation-section">
        <div className="flex items-center gap-2 mb-4">
          <Scale className="h-5 w-5 text-teal-700" />
          <h2 className="font-heading font-bold text-lg">Régularisation annuelle</h2>
        </div>

        {leases.length === 0 ? (
          <div className="text-sm text-slate-400 py-4">Aucun bail actif sur ce bien : créez un bail pour générer un décompte.</div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div className="flex-1 min-w-[220px]">
                <Label className="text-xs">Bail / locataire</Label>
                <Select value={regLease} onValueChange={setRegLease}>
                  <SelectTrigger data-testid="reg-lease-select"><SelectValue placeholder="Choisir un bail" /></SelectTrigger>
                  <SelectContent>
                    {leases.map((l) => <SelectItem key={l.id} value={l.id}>{leaseLabel(l)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[140px]">
                <Label className="text-xs">Année</Label>
                <Select value={regYear} onValueChange={setRegYear}>
                  <SelectTrigger data-testid="reg-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={generate} disabled={!canWrite || !preview} data-testid="generate-reg-btn">
                Générer le décompte
              </Button>
            </div>

            {preview && (
              <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-4 mb-5 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="reg-preview">
                <div>
                  <div className="text-xs text-slate-500">Provisions appelées ({preview.months} mois)</div>
                  <div className="font-heading font-bold text-lg text-slate-900">{fmtEUR(preview.provisions_total)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Charges récupérables réelles</div>
                  <div className="font-heading font-bold text-lg text-slate-900">{fmtEUR(preview.real_recuperable)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">{preview.balance >= 0 ? "Trop-perçu à restituer" : "Complément dû"}</div>
                  <div className={`font-heading font-bold text-lg ${preview.balance >= 0 ? "text-teal-700" : "text-rose-600"}`} data-testid="reg-balance">
                    {fmtEUR(Math.abs(preview.balance))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <h3 className="font-heading font-semibold text-sm text-slate-700 mb-2">Décomptes générés</h3>
        {regs.length === 0 ? (
          <div className="text-sm text-slate-400 py-4">Aucun décompte généré pour ce bien.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Année</TableHead><TableHead>Locataire</TableHead>
                  <TableHead className="text-right">Provisions</TableHead><TableHead className="text-right">Réel</TableHead>
                  <TableHead className="text-right">Solde</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {regs.map((r) => (
                  <TableRow key={r.id} data-testid={`reg-row-${r.id}`}>
                    <TableCell className="font-medium">{r.year}</TableCell>
                    <TableCell>{r.tenant_name || "-"}</TableCell>
                    <TableCell className="text-right">{fmtEUR(r.provisions_total)}</TableCell>
                    <TableCell className="text-right">{fmtEUR(r.real_recuperable)}</TableCell>
                    <TableCell className={`text-right font-medium ${r.balance >= 0 ? "text-teal-700" : "text-rose-600"}`}>
                      {r.balance >= 0 ? "+" : "−"}{fmtEUR(Math.abs(r.balance))}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => downloadReg(r.id, r.year)} data-testid={`download-reg-${r.id}`}><Download className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => delReg(r.id)} disabled={!canWrite} data-testid={`delete-reg-${r.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Dialog ajout/édition dépense */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="charge-dialog">
          <DialogHeader><DialogTitle>{editId ? "Modifier la dépense" : "Nouvelle dépense"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Libellé</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex : Eau parties communes" data-testid="charge-label-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Montant (€)</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="charge-amount-input" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="charge-date-input" />
              </div>
            </div>
            <div>
              <Label>Nature</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="charge-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note (facultatif)</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="charge-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={saveCharge} data-testid="save-charge-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
